import { BASE_URL } from "config/urls";
import { OneOrZeroElementsArray } from "utils/common";
import { get, post, useFetch, useInfiniteFetch } from "utils/sdk";
import { formatGetParams } from "utils/urls";

import { CompanySectionAutomaticFillStatus } from "entities/Checklist/constants";
import { IFieldTemplateMeta, IPackageDetails } from "entities/Checklist/sdk";
import { CompanyFieldFormatType, EFieldStatus } from "entities/Field/constants";
import { FieldType } from "entities/Field/sdk";
import { IFile } from "entities/File/sdk";
import { ITypeOfFilter } from "entities/Task/sdk";

import { ChecklistSnapshotFileStatus } from "./constants";

export enum EShareTokenStatus {
  PENDING = "Pending",
  ACCEPTED = "Accepted",
  REJECTED = "Rejected",
  CANCELED = "Canceled",
  INVALIDATED = "Invalidated",
}

export enum EPartialUserAccessType {
  READ_ONLY = "Read only access",
  READ_WRITE_ACCESS = "Read/Write access",
}

export interface IShareToken {
  token: string;
  created_by: {
    first_name: string;
    last_name: string;
    company: {
      name: string;
    };
  };
  deal: {
    id: number;
    name: string;
  };
  company_package: {
    id: number;
    name: string;
  };
  email: string;
  status: EShareTokenStatus;
}

export interface IPackageDealValue {
  value_id: number;
  default_value: string;
  selected_value: string;
  column_value: {} | Array<{ [key: string]: string }>; // Api is inconsistent here
  rich_value: string;
  file: IFile | null;
  updated_at: string;
}

export interface IFieldOption {
  label: string;
  value: string;
}

interface IBaseRemark {
  id: number;
  updated_at: string;
  created_by: {
    first_name: string;
    last_name: string;
  };
  updated_by: {
    first_name: string;
    last_name: string;
  };
}

export interface ISharedRemark extends IBaseRemark {
  shared_remark: string;
}

export interface IPrivateRemark extends IBaseRemark {
  private_remark: string;
}

export interface IPackageField {
  field_id: number;
  name: string;
  field_type: FieldType;
  columns: string[];
  options: Array<IFieldOption>;
  has_new: boolean;
  shared_remarks: OneOrZeroElementsArray<ISharedRemark>;
  private_remarks: OneOrZeroElementsArray<IPrivateRemark>;
  single_persona: boolean;
  external_instructions?: string | null;
  internal_instructions?: string | null;
  status: EFieldStatus;

  is_linked: boolean;
  is_overridden: boolean;

  values: IPackageDealValue[];
  template?: string;
  template_meta?: Array<IFieldTemplateMeta>;
  field_format_type: CompanyFieldFormatType;
}

export interface IPackageSection {
  section_id: number;
  name: string;
  fields: IPackageField[];
  latest_company_section_automatic_fill_id: number | null;
  latest_company_section_automatic_fill_status: CompanySectionAutomaticFillStatus | null;
  latest_company_section_automatic_fill_file_ids: Array<number>;
  updated_field_count: number;
  external_instructions?: string | null;
  internal_instructions?: string | null;
}

export interface IPackageCategory {
  category_id: number;
  name: string;
  sections: IPackageSection[];
  updated_field_count: number;
  external_instructions?: string | null;
  internal_instructions?: string | null;
}

export interface IPackageTab {
  tab_id: number;
  name: string;
  updated_field_count: number;
  categories: IPackageCategory[];
}

export interface IPackage {
  name: string;
  company: { id: number; name: string } | null;
  tabs: Array<IPackageTab>;
  is_owned: boolean;
  include_submission_button: boolean;
  is_onboarding?: boolean;
}

export interface IFieldValueHistoryItem {
  id: number;
  default_value: string | null;
  selected_value: string | null;
  field_format_type: CompanyFieldFormatType;
  deleted_at: string | null;
  deleted_by: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
  column_value: Array<{ [key: string]: string }> | null;
  rich_value: string | null;
  file: IFile | null;
  uploaded_at: string;
  creator: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
}

export interface ICompanyCustomFieldParsedValue {
  value: string | undefined | null;
  field_id: number;
}

export interface IDealPackage {
  package_id: number;
  name: string;
  company?: {
    name: string;
  };
  is_onboarding: boolean;
}

export const useDealPackagesList = ({ dealId }: { dealId: number }) =>
  useFetch<Array<IDealPackage>>(
    `v2/due-diligence/deal/${dealId}/package/list/`
  );

export const fieldMarkAsSeenBulk = ({
  dealId,
  fieldIds,
  packageId,
}: {
  dealId: number;
  fieldIds: number[];
  packageId: number;
}) =>
  post(
    `${BASE_URL}v2/deal/${dealId}/package/${packageId}/custom-field-value/bulk-mark-as-seen/`,
    {
      field_ids: fieldIds,
    }
  );

export const fieldUpdateNAStatusBulk = ({
  dealId,
  fieldIds,
  naStatus,
}: {
  dealId: number;
  fieldIds: number[];
  naStatus: boolean;
}) =>
  post(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/field-multiple-n-a-status/`,
    {
      field_ids: fieldIds,
      n_a_status: naStatus,
    }
  );

export const usePackageDealDetails = ({
  packageId,
  dealId,
  queryParams,
}: {
  packageId: number;
  dealId: number;
  queryParams?: ITypeOfFilter;
}) =>
  useFetch<IPackage>(
    `v2/due-diligence/package/${packageId}/deal/${dealId}/details/${
      queryParams ? formatGetParams(queryParams) : ""
    }`,
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

export const packageShare = ({
  fieldIds,
  invitations,
  message,
  packageId,
  dealId,
}: {
  fieldIds: Array<number>;
  invitations: Array<{ email: string; access_type: string }>;
  message: string;
  packageId: number;
  dealId: number;
}): Promise<{}> =>
  post(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/package/${packageId}/share/`,
    {
      field_ids: fieldIds,
      invitations,
      message,
    }
  );

export const useShareToken = ({ token }: { token: string }) =>
  useFetch<IShareToken>(`v2/due-diligence/share-token/${token}/retrieve/`);

export const shareTokenAccept = ({ token }: { token: string }) =>
  post<IShareToken>(`${BASE_URL}v2/due-diligence/share-token/${token}/accept/`);

export const shareTokenReject = ({ token }: { token: string }) =>
  post<IShareToken>(`${BASE_URL}v2/due-diligence/share-token/${token}/reject/`);

export const shareTokenUpdate = ({
  shareTokenId,
  data,
}: {
  shareTokenId: number;
  data: { field_ids: Array<number>; access_type: EPartialUserAccessType };
}) =>
  post(`${BASE_URL}v2/due-diligence/share-token/${shareTokenId}/update/`, data);

export const lenderShareChecklist = ({
  packageId,
  data,
}: {
  packageId: number;
  data: { name: string; notification_emails: string[] };
}): Promise<{ token: string }> =>
  post(`${BASE_URL}v2/due-diligence/package/${packageId}/share/`, data);

export const useFieldValuesHistory = ({
  dealId,
  fieldId,
}: {
  dealId: number;
  fieldId: number;
}) =>
  useFetch<Array<IFieldValueHistoryItem>>(
    `v2/due-diligence/deal/${dealId}/field/${fieldId}/value/list`,
    {
      dedupingInterval: 0,
    }
  );

export interface IDueDiligenceMappedData {
  id: number;
  value: string;
  company_custom_field: { id: number };
}

export interface IShareTokenList {
  id: number;
  access_type: EPartialUserAccessType;
  email: string;
  persona: string;
  status: EShareTokenStatus;
  fields: Array<{ id: number }>;
}

export const useShareTokenList = ({
  dealId,
  companyPackageId,
  disabled,
}: {
  dealId: number | null;
  companyPackageId: number | null;
  disabled: boolean;
}) =>
  useFetch<Array<IShareTokenList>>(
    !disabled
      ? `v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/share-token/list/`
      : undefined
  );

export interface IPartialUserAccess {
  id: number;
  access_type: EPartialUserAccessType;
  persona: string;
  is_active: boolean;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    avatar: string | null;
    company: {
      name: string;
    };
  };
  company_custom_fields: Array<{ id: number }>;
}
export const usePartialAccessUsersList = ({
  dealId,
  companyPackageId,
  disabled,
}: {
  dealId: number | null;
  companyPackageId: number | null;
  disabled: boolean;
}) =>
  useFetch<Array<IPartialUserAccess>>(
    !disabled
      ? `v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-custom-field-user-access/list/`
      : undefined
  );

export const updatePartialUserAccess = ({
  userAccessId,
  data,
}: {
  userAccessId: number;
  data: {
    company_custom_field_ids: Array<number>;
    access_type: EPartialUserAccessType;
  };
}) =>
  post(
    `${BASE_URL}v2/due-diligence/company-custom-field-user-access/${userAccessId}/update/`,
    data
  );

export const deactivatePartialUserAccess = ({
  userAccessId,
}: {
  userAccessId: number;
}) =>
  post(
    `${BASE_URL}v2/due-diligence/company-custom-field-user-access/${userAccessId}/deactivate/`
  );

export const resendInvitationShareToken = ({
  shareTokenId,
  email,
}: {
  shareTokenId: number;
  email: string;
}) =>
  post(`${BASE_URL}v2/due-diligence/share-token/${shareTokenId}/resend/`, {
    email,
  });

export const cancelShareToken = ({ shareTokenId }: { shareTokenId: number }) =>
  post(`${BASE_URL}v2/due-diligence/share-token/${shareTokenId}/cancel/`);

export interface ICompanyChecklist {
  id: number;
  name: string;
  deals_count: number;
  fields_count: number;
  linked_fields_count: number;
  source: {
    id: number;
    name: string;
  };
}

export const useCompanyChecklists = () =>
  useInfiniteFetch<ICompanyChecklist>({
    url: `${BASE_URL}v2/due-diligence/company-package/list-for-company/`,
  });

export const useDuplicatedCompanyChecklists = () =>
  useInfiniteFetch<ICompanyChecklist>({
    url: `${BASE_URL}v2/due-diligence/company-package/list-duplicated-for-company/`,
  });

export const useSharedCompanyChecklists = () =>
  useInfiniteFetch<ICompanyChecklist>({
    url: `${BASE_URL}v2/due-diligence/company-package/list/shared/`,
  });

export interface IPublicChecklist {
  id: number;
  name: string;
  fields_count: string;
  company: {
    id: number;
    name: string;
  };
}

export const usePublicChecklists = () =>
  useFetch<Array<IPublicChecklist>>(
    `v2/due-diligence/company-package/list/public`
  );

export const cloneCompanyChecklist = ({
  checklistId,
  newChecklistName,
}: {
  checklistId: number;
  newChecklistName: string;
}) =>
  post<{ id: number }>(
    `${BASE_URL}v2/due-diligence/company-package/${checklistId}/duplicate/`,
    {
      name: newChecklistName,
    }
  );

export const renameCompanyChecklist = ({
  name,
  packageId,
}: {
  name: string;
  packageId: number;
}) =>
  post(`${BASE_URL}v2/due-diligence/company-package/${packageId}/rename/`, {
    name,
  });

export const deleteCompanyChecklist = ({ packageId }: { packageId: number }) =>
  post(`${BASE_URL}v2/due-diligence/company-package/${packageId}/delete/`);

export interface ICompanyPackageDetails extends IPackageDetails {
  is_owned: boolean;
}

export const useCompanyChecklistPreview = ({
  packageId,
}: {
  packageId: number;
}) =>
  useFetch<ICompanyPackageDetails>(
    `v2/due-diligence/company-package/${packageId}/details-retrieve/`
  );

export const useCompanyPackageCheckIsDeletable = ({
  packageId,
  disabled,
}: {
  packageId: number;
  disabled: boolean;
}) =>
  useFetch<{ is_deletable: boolean }>(
    disabled
      ? undefined
      : `v2/due-diligence/company-package/${packageId}/check-is-deletable/`
  );

export interface ICompanyPackageLeftMenuData {
  id: number;
  name: string;
  company: {
    name: string;
  };
  tabs: Array<{
    id: number;
    name: string;
    updated_field_count: number;
  }>;
}

export const useCompanyPackageWithTabsInformation = ({
  packageId,
  dealId,
}: {
  packageId: number;
  dealId: number;
}) =>
  useFetch<ICompanyPackageLeftMenuData>(
    `v2/due-diligence/package/${packageId}/deal/${dealId}/name-company-tabs/retrieve/`,
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

export const fetchCompanyPackageWithTabsInformation = ({
  packageId,
  dealId,
}: {
  packageId: number;
  dealId: number;
}): Promise<ICompanyPackageLeftMenuData> =>
  get(
    `${BASE_URL}v2/due-diligence/package/${packageId}/deal/${dealId}/name-company-tabs/retrieve/`
  );

export const useCompanyPackageStructure = ({
  packageId,
  dealId,
}: {
  packageId: number;
  dealId: number;
}) =>
  useFetch<IPackage>(
    `v2/due-diligence/package/${packageId}/deal/${dealId}/structure/retrieve/`
  );

export const useCompanyPackageStatusCounts = ({
  dealId,
  packageId,
  enabled = true,
}: {
  dealId: number;
  packageId: number;
  enabled?: boolean;
}) =>
  useFetch<{
    total_count: number;
    satisfied_count: number;
    in_review_count: number;
    done_count: number;
    not_done_count: number;
  }>(
    enabled
      ? `v2/deal/${dealId}/company-package/${packageId}/status-counts-retrieve/`
      : undefined
  );

export const fieldStatusBulkUpdate = ({
  dealId,
  fieldIds,
  companyPackageId,
  status,
}: {
  dealId: number;
  fieldIds: Array<number>;
  companyPackageId: number;
  status: EFieldStatus;
}) =>
  post(
    `${BASE_URL}v2/deal/${dealId}/company-package/${companyPackageId}/company-custom-field/status-bulk-update/`,
    { field_ids: fieldIds, status }
  );

export interface ICompanyPackageSnapshot {
  id: number;
  name: string;
  created_by: {
    first_name: string;
    last_name: string;
  };
  created_at: string;
  pdf_file: {
    original_file_name: string;
  };
  dd_zip_file: {
    id: number;
    file: {
      original_file_name: string;
    };
  };
  pdf_file_status: ChecklistSnapshotFileStatus;
  zip_file_status: ChecklistSnapshotFileStatus;
}

export const useCompanyPackageSnapshotList = ({
  dealId,
  companyPackageId,
}: {
  dealId: number;
  companyPackageId: number;
}) =>
  useFetch<Array<ICompanyPackageSnapshot>>(
    `v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/list/`
  );

export const useCompanyPackageSnapshot = ({
  dealId,
  companyPackageId,
  snapshotId,
  enabled = false,
}: {
  dealId: number;
  companyPackageId: number;
  snapshotId: number;
  enabled?: boolean;
}) =>
  useFetch<ICompanyPackageSnapshot>(
    enabled
      ? `v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/${snapshotId}/retrieve/`
      : undefined
  );

export const snapshotPDFFileSignedURLRetrieve = ({
  dealId,
  companyPackageId,
  snapshotId,
}: {
  dealId: number;
  companyPackageId: number;
  snapshotId: number;
}): Promise<string> =>
  get(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/${snapshotId}/pdf-file-signed-url/retrieve/`
  );

export const snapshotZIPFileSignedURLRetrieve = ({
  dealId,
  companyPackageId,
  zipId,
}: {
  dealId: number;
  companyPackageId: number;
  zipId: number;
}): Promise<string> =>
  get(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/zip/${zipId}/zip-file-signed-url/retrieve/`
  );

export const companyPackageSnapshotRename = ({
  dealId,
  companyPackageId,
  snapshotId,
  name,
}: {
  dealId: number;
  companyPackageId: number;
  snapshotId: number;
  name: string;
}) =>
  post(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/${snapshotId}/rename/`,
    { name }
  );

export const companyPackageSnapshotDelete = ({
  dealId,
  companyPackageId,
  snapshotId,
}: {
  dealId: number;
  companyPackageId: number;
  snapshotId: number;
}) =>
  post(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/${snapshotId}/delete/`
  );

export const companyPackageSnapshotCreate = ({
  dealId,
  companyPackageId,
  name,
  html,
}: {
  dealId: number;
  companyPackageId: number;
  html: string;
  name?: string;
}) =>
  post<{
    id: number;
    created_at: string;
    created_by: { first_name: string; last_name: string };
  }>(
    `${BASE_URL}v2/due-diligence/deal/${dealId}/company-package/${companyPackageId}/company-package-snapshot/create/`,
    { name, html }
  );
